import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, ArrowLeft, FileText, Plus, ScanLine, Send, Trash2, Truck } from 'lucide-react';
import Button from '../ui/Button';
import BarcodeScanner from '../shared/BarcodeScanner';
import FormModal, { Field, inputClass } from '../fieldWork/FormModal';
import harvestService from '../../services/harvest';
import { errorMessage } from '../../utils/apiResponse';
import { formatDateTimeInTz, useOrgTimezone } from '../../utils/timezone';
import { binPlace, loadLabel, type AddBinResult, type HarvestLoad, type LoadInput } from '../../types/harvest';
import { Notice } from '../labour/shared';

const STATUS: Record<string, { label: string; cls: string }> = {
  building: { label: 'Loading', cls: 'bg-sky-50 text-sky-700' },
  dispatched: { label: 'Dispatched', cls: 'bg-indigo-50 text-indigo-700' },
  delivered: { label: 'Delivered', cls: 'bg-green-50 text-green-700' },
};

const RESULT_TEXT: Record<string, string> = {
  added: 'on', already: 'already on', other_load: '', not_tagged: '', void: '', not_found: '', invalid: '',
};

/**
 * Loads: tagged bins scanned onto a truck for a packhouse, a dispatch docket
 * that names any bin picked inside a spray withholding period, then
 * dispatched and delivered. "Enter packout" opens the packout form with the
 * load's bins already chosen.
 */
export default function LoadsPanel({ onEnterPackout }: { onEnterPackout: (loadId: number) => void }) {
  const [loads, setLoads] = useState<HarvestLoad[]>([]);
  const [openId, setOpenId] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoads(await harvestService.listLoads());
      setError(null);
    } catch (e) {
      setError(errorMessage(e, 'Could not load the loads'));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (openId) {
    return <LoadDetail id={openId} onBack={() => { setOpenId(null); load(); }} onEnterPackout={onEnterPackout} />;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-neutral-500">Scan tagged bins onto a load, print its docket, send it.</p>
        <Button size="sm" variant="primary" onClick={() => setCreating(true)}><Plus className="h-4 w-4" /> New load</Button>
      </div>
      {error && <Notice tone="error">{error}</Notice>}
      {loads.length === 0 ? (
        <p className="text-sm text-neutral-500 py-6 text-center">No loads yet.</p>
      ) : (
        <ul className="divide-y divide-neutral-100 border border-neutral-200 rounded-lg" aria-label="Loads">
          {loads.map(l => (
            <li key={l.id}>
              <button type="button" onClick={() => setOpenId(l.id)} className="w-full text-left px-3 py-3 flex flex-wrap items-center gap-x-3 gap-y-1">
                <Truck className="h-4 w-4 text-indigo-600 flex-shrink-0" />
                <span className="font-mono font-semibold text-neutral-900">{loadLabel(l)}</span>
                <span className="flex-1 min-w-[8rem] text-sm text-neutral-700 truncate">{l.destination_name || 'No destination yet'}</span>
                <span className="text-xs text-neutral-500">{l.bin_count} bins · {Math.round(l.est_kg)} kg</span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS[l.status]?.cls ?? ''}`}>{STATUS[l.status]?.label ?? l.status}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {creating && (
        <LoadFormModal load={null} onClose={() => setCreating(false)} onSaved={l => { setCreating(false); setOpenId(l.id); }} />
      )}
    </div>
  );
}

function LoadFormModal({ load, onClose, onSaved }: { load: HarvestLoad | null; onClose: () => void; onSaved: (l: HarvestLoad) => void }) {
  const [form, setForm] = useState<LoadInput>({
    destination_name: load?.destination_name ?? '', grower_number: load?.grower_number ?? '',
    driver_name: load?.driver_name ?? '', vehicle_text: load?.vehicle_text ?? '', carrier_name: load?.carrier_name ?? '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = (k: keyof LoadInput, v: string) => setForm(f => ({ ...f, [k]: v }));
  const submit = async () => {
    setSaving(true);
    try {
      onSaved(load ? await harvestService.updateLoad(load.id, form) : await harvestService.createLoad(form));
    } catch (e) {
      setError(errorMessage(e, 'Could not save the load'));
    } finally {
      setSaving(false);
    }
  };
  return (
    <FormModal title={load ? `Load ${loadLabel(load)}` : 'New load'} onClose={onClose} onSubmit={submit} submitting={saving} error={error}>
      <Field label="Going to (packhouse)" htmlFor="ld-dest">
        <input id="ld-dest" className={inputClass} value={form.destination_name ?? ''} onChange={e => set('destination_name', e.target.value)} autoFocus />
      </Field>
      <Field label="Grower number" htmlFor="ld-grower">
        <input id="ld-grower" className={inputClass} value={form.grower_number ?? ''} onChange={e => set('grower_number', e.target.value)} />
      </Field>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Driver" htmlFor="ld-driver">
          <input id="ld-driver" className={inputClass} value={form.driver_name ?? ''} onChange={e => set('driver_name', e.target.value)} />
        </Field>
        <Field label="Vehicle / rego" htmlFor="ld-veh">
          <input id="ld-veh" className={inputClass} value={form.vehicle_text ?? ''} onChange={e => set('vehicle_text', e.target.value)} />
        </Field>
      </div>
      <Field label="Carrier (if not your own)" htmlFor="ld-carrier">
        <input id="ld-carrier" className={inputClass} value={form.carrier_name ?? ''} onChange={e => set('carrier_name', e.target.value)} />
      </Field>
    </FormModal>
  );
}

function LoadDetail({ id, onBack, onEnterPackout }: { id: number; onBack: () => void; onEnterPackout: (loadId: number) => void }) {
  const tz = useOrgTimezone();
  const [load, setLoad] = useState<HarvestLoad | null>(null);
  const [tags, setTags] = useState('');
  const [results, setResults] = useState<AddBinResult[]>([]);
  const [scanning, setScanning] = useState(false);
  const [editing, setEditing] = useState(false);
  const [voiding, setVoiding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try { setLoad(await harvestService.getLoad(id)); } catch (e) { setError(errorMessage(e, 'Could not open the load')); }
  }, [id]);
  useEffect(() => { refresh(); }, [refresh]);

  const add = async (codes: string[]) => {
    if (!codes.length) return;
    try {
      const r = await harvestService.addBinsToLoad(id, { tags: codes });
      setResults(prev => [...r.results, ...prev].slice(0, 20));
      setLoad(r.load);
      setError(null);
    } catch (e) {
      setError(errorMessage(e, 'Could not add the bins'));
    }
  };

  const act = async (fn: () => Promise<HarvestLoad | void>) => {
    try {
      const r = await fn();
      if (r) setLoad(r);
      setError(null);
    } catch (e) {
      setError(errorMessage(e, 'That did not work'));
    }
  };

  if (!load) return <div className="text-sm text-neutral-500 py-6 text-center">{error ?? 'Loading…'}</div>;
  const building = load.status === 'building';
  const bins = load.bins ?? [];
  const within = bins.filter(b => b.whp.status === 'within_whp');

  return (
    <div className="space-y-4">
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-neutral-500 hover:text-neutral-700"><ArrowLeft className="h-4 w-4" /> All loads</button>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-neutral-900">Load {loadLabel(load)} <span className={`ml-2 text-xs px-2 py-0.5 rounded-full align-middle ${STATUS[load.status]?.cls ?? ''}`}>{STATUS[load.status]?.label}</span></h3>
          <p className="text-sm text-neutral-600">
            {load.destination_name || 'No destination'}{load.grower_number ? ` · grower ${load.grower_number}` : ''}
            {load.driver_name ? ` · ${load.driver_name}` : ''}{load.vehicle_text ? ` · ${load.vehicle_text}` : ''}
          </p>
          <p className="text-xs text-neutral-500">
            {bins.length} bins · {Math.round(load.est_kg)} kg
            {load.dispatched_at ? ` · dispatched ${formatDateTimeInTz(load.dispatched_at, tz)}` : ''}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={() => harvestService.downloadDocket(load).catch(e => setError(errorMessage(e, 'Could not make the docket')))}>
            <FileText className="h-4 w-4" /> Docket
          </Button>
          {building && <Button size="sm" variant="outline" onClick={() => setEditing(true)}>Details</Button>}
          {building && <Button size="sm" variant="primary" disabled={!bins.length} onClick={() => act(() => harvestService.dispatchLoad(id))}><Send className="h-4 w-4" /> Dispatch</Button>}
          {load.status === 'dispatched' && <Button size="sm" variant="primary" onClick={() => act(() => harvestService.markDelivered(id))}>Mark delivered</Button>}
          {load.status !== 'building' && <Button size="sm" variant="outline" onClick={() => onEnterPackout(id)}>Enter packout</Button>}
          {building && <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => setVoiding(true)}>Void</Button>}
        </div>
      </div>

      {error && <Notice tone="error">{error}</Notice>}
      {within.length > 0 && (
        <Notice tone="warn">
          <span className="flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>{within.length} {within.length === 1 ? 'bin was' : 'bins were'} picked inside a recorded spray withholding period. The docket names {within.length === 1 ? 'it' : 'them'}.</span>
          </span>
        </Notice>
      )}

      {building && (
        <form className="flex flex-wrap items-end gap-2" onSubmit={e => {
          e.preventDefault();
          add(tags.split(/[\s,]+/).map(t => t.trim()).filter(Boolean));
          setTags('');
        }}>
          <label className="text-xs text-neutral-600 flex-1 min-w-[12rem]">
            <span className="block mb-1">Add bins by tag</span>
            <input className={`${inputClass} font-mono`} value={tags} placeholder="4817-6 4818-4 …" onChange={e => setTags(e.target.value)} />
          </label>
          <Button type="submit" size="sm" variant="outline" disabled={!tags.trim()}>Add</Button>
          <Button type="button" size="sm" variant="outline" onClick={() => setScanning(true)}><ScanLine className="h-4 w-4" /> Scan</Button>
        </form>
      )}
      {results.length > 0 && (
        <ul className="text-sm space-y-0.5" aria-label="Scan results">
          {results.map((r, i) => (
            <li key={i} className={r.status === 'added' ? 'text-green-700' : r.status === 'already' ? 'text-neutral-500' : 'text-red-700'}>
              <span className="font-mono">{r.tag_code ?? r.input}</span> — {r.status === 'added' || r.status === 'already' ? `${RESULT_TEXT[r.status]} the load` : r.message}
            </li>
          ))}
        </ul>
      )}

      {bins.length === 0 ? (
        <p className="text-sm text-neutral-500 py-4 text-center">No bins on this load yet.</p>
      ) : (
        <ul className="divide-y divide-neutral-100 border border-neutral-200 rounded-lg" aria-label="Bins on the load">
          {bins.map(b => (
            <li key={b.id} className="px-3 py-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
              <span className={`font-mono font-semibold w-28 ${b.whp.status === 'within_whp' ? 'text-red-700' : 'text-neutral-900'}`}>{b.tag_code}</span>
              <span className="flex-1 min-w-[10rem] text-neutral-700">{binPlace(b)}{b.cultivar_name ? ` · ${b.cultivar_name}` : ''}</span>
              <span className="text-xs text-neutral-500">{b.local_date}</span>
              {b.whp.status === 'within_whp' && <span className="text-xs px-2 py-0.5 rounded-full bg-red-50 text-red-700">Inside WHP</span>}
              {b.whp.status === 'unknown' && <span className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">No block</span>}
              {building && (
                <button type="button" aria-label={`Take ${b.tag_code} off the load`} className="text-neutral-400 hover:text-red-600"
                  onClick={() => act(() => harvestService.removeBinFromLoad(id, b.id))}>
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {scanning && <BarcodeScanner label="Scan bin tags onto the load" onScan={code => add([code])} onClose={() => setScanning(false)} />}
      {editing && <LoadFormModal load={load} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); refresh(); }} />}
      {voiding && (
        <VoidModal title={`Void load ${loadLabel(load)}`} onClose={() => setVoiding(false)}
          onVoid={async reason => { await harvestService.voidLoad(id, reason); onBack(); }} />
      )}
    </div>
  );
}

export function VoidModal({ title, onClose, onVoid }: { title: string; onClose: () => void; onVoid: (reason: string) => Promise<void> }) {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <FormModal title={title} submitLabel="Void" onClose={onClose} submitting={saving} submitDisabled={reason.trim().length < 3} error={error}
      onSubmit={async () => {
        setSaving(true);
        try { await onVoid(reason.trim()); } catch (e) { setError(errorMessage(e, 'Could not void')); } finally { setSaving(false); }
      }}>
      <Field label="Why?" htmlFor="void-reason">
        <input id="void-reason" className={inputClass} value={reason} onChange={e => setReason(e.target.value)} autoFocus />
      </Field>
    </FormModal>
  );
}
